import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Table, Tooltip, Input, Button, Space } from "antd";
import { SorterResult, FilterDropdownProps } from "antd/es/table/interface";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { AnyObject } from "antd/es/_util/type";
import {
  StyledTable,
  TableContainer,
} from "../../components/tables/styledComponents";
import {
  Card,
  RowContainer,
  FlexibleContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  LinkageRecord,
  LinkageRecordsRequest,
  LinkageRecordsResponse,
  LinkageDashboardFilters,
  LinkageColumnFilter,
  AlignmentCategory,
  ManagerRelationship,
  InfluenceDirection,
} from "../../types/linkageDashboardTypes";
import { TagProps, CompassTag } from "../../components/visuals/CompassTag";
import {
  CellLink,
  ExpandableTextCell,
  TableCellType,
  ColumnType as CustomColumnType,
} from "../../models/tableModels";
import {
  CustomTableCell,
  CustomTableCellProps,
} from "../../components/tables/CustomTableCell";
import { CompanyLogoProps } from "../../components/tables/CompanyLogo";
import { Avatar, AvatarProps } from "../../components/visuals/Avatar";
import { usePost } from "../../hooks/usePost";
import { useToast } from "../../store/ToastProvider";
import { formatReadable } from "../../utils/formattingUtils";
import { greyText, darkGreen } from "../../styles";
import { MESSAGES } from "../../messages";
import { ROUTES } from "../../routes/routeNames";

import environmental from "../../assets/svg/environmental_circle.svg";
import social from "../../assets/svg/social_circle.svg";
import governance from "../../assets/svg/governance_circle.svg";
import strategy from "../../assets/svg/strategy_circle.svg";

// Linkage categorisation images
import consistentReinforcementImg from "../../assets/images/engagement_linkage/consistent_reinforcement.png";
import internalInconsistencyImg from "../../assets/images/engagement_linkage/internal_inconsistency.png";
import synergisticAlignmentImg from "../../assets/images/engagement_linkage/synergistic_alignment.png";
import activeUnderminingImg from "../../assets/images/engagement_linkage/active_undermining.png";
import engagementPrecedesVoteImg from "../../assets/images/engagement_linkage/engagement_precedes_vote.png";
import votePrecedesEngagementImg from "../../assets/images/engagement_linkage/vote_precedes_engagement.png";
import bidirectionalImg from "../../assets/images/engagement_linkage/bidirectional.png";
import thematicAlignmentImg from "../../assets/images/engagement_linkage/thematic_alignment.png";
import complementaryActionImg from "../../assets/images/engagement_linkage/complementary_action.png";
import escalationPathImg from "../../assets/images/engagement_linkage/escalation_path.png";
import isolatedActivityImg from "../../assets/images/engagement_linkage/isolated_activity.png";

// =============================================================================
// CONSTANTS
// =============================================================================

const PAGE_SIZE = 25;

import { EngagementType } from "../../models/stewardshipModels";

const META_THEME_ICONS: Record<EngagementType, string> = {
  [EngagementType.Environmental]: environmental,
  [EngagementType.Social]: social,
  [EngagementType.Governance]: governance,
  [EngagementType.StrategyRiskCommunication]: strategy,
};

// ── Icon mappings for categorisations ──

// Tag colors
const ALIGNMENT_TAG_COLORS: Record<
  AlignmentCategory,
  { bg: string; text: string }
> = {
  positive_alignment: { bg: "#DDF6E8", text: "#2e8b57" },
  negative_tension: { bg: "#FFE8EC", text: "#DD1155" },
  other: { bg: "#F2F4F7", text: "#929395" },
};

const RELATIONSHIP_TAG_COLORS: Record<
  ManagerRelationship,
  { bg: string; text: string }
> = {
  same_manager: { bg: "#E0EEFF", text: "#0B759D" },
  cross_manager: { bg: "#FFF4E0", text: "#c67c00" },
};

const DIRECTION_TAG_COLORS: Record<
  InfluenceDirection,
  { bg: string; text: string }
> = {
  engagement_drives_vote: { bg: "#DDF6E8", text: "#2e8b57" },
  vote_triggers_engagement: { bg: "#E0EEFF", text: "#0B759D" },
  bidirectional: { bg: "#FFF4E0", text: "#c67c00" },
  unknown: { bg: "#F2F4F7", text: "#929395" },
};

// Alignment × Relationship → InteractionClass image
const ALIGNMENT_ICONS: Record<string, Record<string, string>> = {
  positive_alignment: {
    same_manager: consistentReinforcementImg,
    cross_manager: synergisticAlignmentImg,
  },
  negative_tension: {
    same_manager: internalInconsistencyImg,
    cross_manager: activeUnderminingImg,
  },
};

const getAlignmentIcon = (
  alignment: AlignmentCategory,
  relationship: ManagerRelationship,
): string | undefined => ALIGNMENT_ICONS[alignment]?.[relationship];

// Direction → SequenceFlow image
const DIRECTION_ICONS: Record<InfluenceDirection, string | undefined> = {
  engagement_drives_vote: engagementPrecedesVoteImg,
  vote_triggers_engagement: votePrecedesEngagementImg,
  bidirectional: bidirectionalImg,
  unknown: undefined,
};

// LinkageType images
const LINKAGE_TYPE_ICONS: Record<string, string> = {
  THEMATIC_ALIGNMENT: thematicAlignmentImg,
  COMPLEMENTARY_ACTION: complementaryActionImg,
  ESCALATION_PATH: escalationPathImg,
  ISOLATED_ACTIVITY: isolatedActivityImg,
};

const LINKAGE_TYPE_TAG_COLORS: Record<string, { bg: string; text: string }> = {
  THEMATIC_ALIGNMENT: { bg: "#E8F0FE", text: "#1A5DAB" },
  COMPLEMENTARY_ACTION: { bg: "#DDF6E8", text: "#2e8b57" },
  ESCALATION_PATH: { bg: "#FFF4E0", text: "#c67c00" },
  ISOLATED_ACTIVITY: { bg: "#F2F4F7", text: "#929395" },
};

// ── Enum filter checkbox options (server-side) ──

const ENUM_FILTER_OPTIONS: Record<string, { text: string; value: string }[]> = {
  manager_relationship: [
    { text: "Same Manager", value: "same_manager" },
    { text: "Cross Manager", value: "cross_manager" },
  ],
  linkage_type: [
    { text: "Thematic Alignment", value: "THEMATIC_ALIGNMENT" },
    { text: "Complementary Action", value: "COMPLEMENTARY_ACTION" },
    { text: "Escalation Path", value: "ESCALATION_PATH" },
    { text: "Isolated Activity", value: "ISOLATED_ACTIVITY" },
  ],
  influence_direction: [
    { text: "Engagement Precedes Vote", value: "ENGAGEMENT_PRECEDES_VOTE" },
    { text: "Vote Precedes Engagement", value: "VOTE_PRECEDES_ENGAGEMENT" },
    { text: "Bidirectional", value: "BIDIRECTIONAL" },
  ],
  alignment_category: [
    { text: "Consistent Reinforcement", value: "CONSISTENT_REINFORCEMENT" },
    { text: "Synergistic Alignment", value: "SYNERGISTIC_ALIGNMENT" },
    { text: "Internal Inconsistency", value: "INTERNAL_INCONSISTENCY" },
    { text: "Active Undermining", value: "ACTIVE_UNDERMINING" },
  ],
};

// Map column keys → backend filter keys for enum columns
const ENUM_COL_TO_FILTER: Record<string, string> = {
  relationship: "manager_relationship",
  linkageType: "linkage_type",
  direction: "influence_direction",
  alignment: "alignment_category",
};

// Map granular UI values → backend aggregate values
const ENUM_VALUE_TO_BACKEND: Record<string, string> = {
  // interaction_class → alignment_category
  CONSISTENT_REINFORCEMENT: "positive_alignment",
  SYNERGISTIC_ALIGNMENT: "positive_alignment",
  INTERNAL_INCONSISTENCY: "negative_tension",
  ACTIVE_UNDERMINING: "negative_tension",
  // sequence_flow → influence_direction
  ENGAGEMENT_PRECEDES_VOTE: "engagement_drives_vote",
  VOTE_PRECEDES_ENGAGEMENT: "vote_triggers_engagement",
  BIDIRECTIONAL: "bidirectional",
};

// =============================================================================
// DATA SOURCE
// =============================================================================

// Composite cell: standalone avatar image + colored tag with text
interface AvatarTagCell {
  image: string | null;
  tag: TagProps;
  rationale?: string;
}

// Dual-avatar cell: two avatars side by side (engagement manager | voting manager)
interface DualAvatarCell {
  engagementManager: AvatarProps;
  votingManager: AvatarProps;
}

interface LinkageDataSource {
  key: string | number;
  company: CompanyLogoProps;
  managers: DualAvatarCell;
  theme: string;
  metaTheme: AvatarProps;
  alignment: AvatarTagCell;
  relationship: AvatarTagCell;
  linkageType: AvatarTagCell;
  direction: AvatarTagCell;
  engagementTitle: CellLink;
  votingTitle: CellLink;
  rationale: ExpandableTextCell;
  topic: string;
  engagementDate: string;
  votingDate: string;
  [key: string]:
    | string
    | number
    | CompanyLogoProps
    | AvatarProps
    | DualAvatarCell
    | AvatarTagCell
    | CellLink
    | ExpandableTextCell
    | undefined;
}

const buildDataSource = (records: LinkageRecord[]): LinkageDataSource[] =>
  records.map((r, i) => {
    const alignmentIcon = getAlignmentIcon(
      r.alignment_category,
      r.manager_relationship,
    );
    const directionIcon = DIRECTION_ICONS[r.influence_direction];

    return {
      key: r.linkage_id || `${r.compass_cid}-${i}`,
      company: {
        name: r.company_name,
        logoUrl: r.company_logo || "",
        companyId: r.compass_cid,
      },
      managers: {
        engagementManager: {
          username: r.engagement_manager_name,
          image: r.engagement_manager_image || null,
          size: "default" as const,
          tooltip: true,
        },
        votingManager: {
          username: r.voting_manager_name,
          image: r.voting_manager_image || null,
          size: "default" as const,
          tooltip: true,
        },
      },
      theme: formatReadable(r.theme),
      metaTheme: {
        username: r.meta_theme,
        image: META_THEME_ICONS[r.meta_theme] || strategy,
        displayText: r.meta_theme,
        size: "default" as const,
        tooltip: true,
      },
      alignment: {
        image: alignmentIcon || null,
        tag: {
          tagKey: r.alignment_category,
          displayValue: r.interaction_class
            ? formatReadable(r.interaction_class)
            : formatReadable(r.alignment_category),
          color: (
            ALIGNMENT_TAG_COLORS[r.alignment_category] ||
            ALIGNMENT_TAG_COLORS.other
          ).text,
          bg: (
            ALIGNMENT_TAG_COLORS[r.alignment_category] ||
            ALIGNMENT_TAG_COLORS.other
          ).bg,
        },
        rationale: r.alignment_rationale,
      },
      relationship: {
        image: null,
        tag: {
          tagKey: r.manager_relationship,
          displayValue: formatReadable(r.manager_relationship),
          color: (
            RELATIONSHIP_TAG_COLORS[r.manager_relationship] ||
            RELATIONSHIP_TAG_COLORS.same_manager
          ).text,
          bg: (
            RELATIONSHIP_TAG_COLORS[r.manager_relationship] ||
            RELATIONSHIP_TAG_COLORS.same_manager
          ).bg,
        },
      },
      linkageType: {
        image: r.linkage_type
          ? LINKAGE_TYPE_ICONS[r.linkage_type] || null
          : null,
        tag: {
          tagKey: r.linkage_type || "unknown",
          displayValue: r.linkage_type
            ? formatReadable(r.linkage_type)
            : "Unknown",
          color: (
            LINKAGE_TYPE_TAG_COLORS[r.linkage_type || ""] || {
              text: "#929395",
            }
          ).text,
          bg: (
            LINKAGE_TYPE_TAG_COLORS[r.linkage_type || ""] || {
              bg: "#F2F4F7",
            }
          ).bg,
        },
        rationale: r.rationale,
      },
      direction: {
        image: directionIcon || null,
        tag: {
          tagKey: r.influence_direction,
          displayValue: r.sequence_flow
            ? formatReadable(r.sequence_flow)
            : formatReadable(r.influence_direction),
          color: (
            DIRECTION_TAG_COLORS[r.influence_direction] ||
            DIRECTION_TAG_COLORS.unknown
          ).text,
          bg: (
            DIRECTION_TAG_COLORS[r.influence_direction] ||
            DIRECTION_TAG_COLORS.unknown
          ).bg,
        },
        rationale: r.influence_direction_rationale,
      },
      topic: r.topic_name ? formatReadable(r.topic_name) : "-",
      engagementTitle: {
        displayValue: r.engagement_title || "-",
        link: r.engagement_id
          ? `${ROUTES.SINGLE_ENGAGEMENT}/${r.engagement_id}`
          : "",
        newTab: true,
      },
      votingTitle: {
        displayValue: r.voting_title || "-",
        link:
          r.compass_cid && r.voting_record_id
            ? `${ROUTES.COMPANY_PROFILE}/${r.compass_cid}?votingRecordId=${r.voting_record_id}`
            : "",
        newTab: true,
      },
      engagementDate: r.engagement_date || "-",
      votingDate: r.voting_date || "-",
      rationale: {
        text: r.rationale || "-",
        maxLines: 2,
      },
    };
  });

// =============================================================================
// COLUMNS
// =============================================================================

interface BaseColumnDef {
  title: string;
  key: string;
  dataIndex: string;
  fixed?: "left" | "right";
  sorter?: boolean;
  originalKey?: string;
  /** Backend key used in column_filters — omit to disable column filter */
  filterKey?: string;
}

const COLUMNS: BaseColumnDef[] = [
  // ── Identity ──
  {
    title: "Company",
    key: "company",
    dataIndex: "company",
    fixed: "left",
    sorter: true,
    originalKey: "company_name",
  },
  {
    title: "Engagement / Voting Manager",
    key: "managers",
    dataIndex: "managers",
    fixed: "left",
    sorter: true,
    originalKey: "engagement_manager_name",
  },
  // ── Source records ──
  {
    title: "Engagement",
    key: "engagementTitle",
    dataIndex: "engagementTitle",
    sorter: true,
    originalKey: "engagement_title",
    filterKey: "engagement_title",
  },
  {
    title: "Vote",
    key: "votingTitle",
    dataIndex: "votingTitle",
    sorter: true,
    originalKey: "voting_title",
    filterKey: "voting_title",
  },
  // ── AI Classification ──
  {
    title: "AI Rationale",
    key: "rationale",
    dataIndex: "rationale",
  },
  // ── Subject ──
  {
    title: "Category",
    key: "metaTheme",
    dataIndex: "metaTheme",
    sorter: true,
    originalKey: "meta_theme",
    filterKey: "meta_theme",
  },
  {
    title: "Theme",
    key: "theme",
    dataIndex: "theme",
    sorter: true,
    originalKey: "theme",
    filterKey: "theme",
  },
  {
    title: "Alignment",
    key: "alignment",
    dataIndex: "alignment",
    sorter: true,
    originalKey: "alignment_category",
    filterKey: "alignment_category",
  },
  {
    title: "Manager Link",
    key: "relationship",
    dataIndex: "relationship",
    sorter: true,
    originalKey: "manager_relationship",
    filterKey: "manager_relationship",
  },
  {
    title: "Linkage Type",
    key: "linkageType",
    dataIndex: "linkageType",
    sorter: true,
    originalKey: "linkage_type",
    filterKey: "linkage_type",
  },
  {
    title: "Direction",
    key: "direction",
    dataIndex: "direction",
    sorter: true,
    originalKey: "influence_direction",
    filterKey: "influence_direction",
  },
  // ── Dates ──
  {
    title: "Engagement Date",
    key: "engagementDate",
    dataIndex: "engagementDate",
    sorter: true,
    originalKey: "engagement_date",
  },
  {
    title: "Voting Date",
    key: "votingDate",
    dataIndex: "votingDate",
    sorter: true,
    originalKey: "voting_date",
  },
];

const COLUMN_TYPES: CustomColumnType[] = [
  { type: TableCellType.COMPANY_LOGO, columnKey: "company" },
  { type: TableCellType.AVATAR, columnKey: "managers" },
  { type: TableCellType.AVATAR, columnKey: "metaTheme" },
  { type: TableCellType.TEXT, columnKey: "theme" },
  { type: TableCellType.TEXT, columnKey: "topic" },
  { type: TableCellType.LINK, columnKey: "engagementTitle" },
  { type: TableCellType.LINK, columnKey: "votingTitle" },
  { type: TableCellType.DATE, columnKey: "engagementDate" },
  { type: TableCellType.DATE, columnKey: "votingDate" },
  { type: TableCellType.EXPANDABLE_TEXT, columnKey: "rationale" },
];

// Columns that render Avatar (image only) + Tag (colored with text)
const AVATAR_TAG_COLUMNS = new Set([
  "alignment",
  "relationship",
  "direction",
  "linkageType",
]);

const getColumnType = (
  columnKey: string,
  isLoading: boolean,
): CustomColumnType => {
  if (isLoading) return { columnKey, type: TableCellType.LOADING };
  const match = COLUMN_TYPES.find((c) => c.columnKey === columnKey);
  return match || { columnKey, type: TableCellType.TEXT };
};

// =============================================================================
// COMPONENT
// =============================================================================

interface LinkageTableProps {
  filters: LinkageDashboardFilters;
  enumFilters: Record<string, string[]>;
  setEnumFilters: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
}

export const LinkageTable = ({
  filters,
  enumFilters,
  setEnumFilters,
}: LinkageTableProps): JSX.Element => {
  const { addToast } = useToast();
  const { sendData, isLoading } = usePost<
    LinkageRecordsResponse,
    LinkageRecordsRequest
  >();

  const [records, setRecords] = useState<LinkageRecord[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // ── Per-column filters ──
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );
  // Draft state for live typing (not yet committed)
  const [draftColumnFilters, setDraftColumnFilters] = useState<
    Record<string, string>
  >({});

  const activeColumnFilters = useMemo<LinkageColumnFilter[]>(() => {
    const textEntries = Object.entries(columnFilters)
      .filter(([, v]) => v.trim().length > 0)
      .map(([key, value]) => ({ key, value: value.trim() }));
    // Enum filters also need to go as column_filters for the records endpoint
    const enumEntries = Object.entries(enumFilters)
      .filter(([, vals]) => vals.length > 0)
      .flatMap(([colKey, vals]) => {
        const backendKey = ENUM_COL_TO_FILTER[colKey] || colKey;
        // Deduplicate mapped values (e.g. two interaction_class values may map to same alignment_category)
        const mappedVals = vals
          .map((v) => ENUM_VALUE_TO_BACKEND[v] || v)
          .filter((v, i, a) => a.indexOf(v) === i);
        return mappedVals.map((v) => ({ key: backendKey, value: v }));
      });
    return [...textEntries, ...enumEntries];
  }, [columnFilters, enumFilters]);

  // ── Current sort ──
  const [currentSort, setCurrentSort] = useState<{
    key: string;
    sort: "asc" | "desc";
  } | null>(null);

  const dataSource = useMemo(() => buildDataSource(records), [records]);

  // Refs for values used inside fetchRecords so it stays stable
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;
  const columnFiltersRef = useRef(activeColumnFilters);
  columnFiltersRef.current = activeColumnFilters;
  const currentSortRef = useRef(currentSort);
  currentSortRef.current = currentSort;

  const fetchRecords = useCallback(
    async (
      page: number,
      sort: { key: string; sort: "asc" | "desc" } | null,
    ): Promise<void> => {
      try {
        const payload: LinkageRecordsRequest = {
          ...filtersRef.current,
          pagination: { page, page_size: PAGE_SIZE },
          sort,
          column_filters:
            columnFiltersRef.current.length > 0
              ? columnFiltersRef.current
              : null,
        };
        const res = await sendData("GET_LINKAGE_RECORDS", payload);
        if (res) {
          setRecords(res.records);
          setTotal(res.total);
        }
      } catch {
        addToastRef.current("Failed to fetch linkage records", "error");
      }
    },
    [sendData],
  );

  // ── Unified filter key: top-level + column filters ──
  const filterKey = useMemo(
    () => JSON.stringify({ filters, columnFilters: activeColumnFilters }),
    [filters, activeColumnFilters],
  );
  const prevFilterKey = useRef(filterKey);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      // Initial fetch
      isInitialMount.current = false;
      fetchRecords(1, null);
      return;
    }
    if (prevFilterKey.current !== filterKey) {
      const prevParsed = JSON.parse(prevFilterKey.current);
      const currParsed = JSON.parse(filterKey);
      const topLevelChanged =
        JSON.stringify(prevParsed.filters) !==
        JSON.stringify(currParsed.filters);

      prevFilterKey.current = filterKey;
      setCurrentPage(1);

      if (topLevelChanged) {
        // Top-level filter change → reset text filters and sort
        setCurrentSort(null);
        setColumnFilters({});
        setDraftColumnFilters({});
        fetchRecords(1, null);
      } else {
        // Column filter change → keep current sort
        fetchRecords(1, currentSortRef.current);
      }
    }
  }, [filterKey]);

  const handleTableChange = (
    pagination: { current?: number },
    _filters: Record<string, unknown>,
    sorter: SorterResult<AnyObject>,
  ): void => {
    // ── Extract enum filter selections from antd ──
    const newEnumFilters: Record<string, string[]> = {};
    for (const colKey of Object.keys(ENUM_COL_TO_FILTER)) {
      const vals = _filters[colKey] as string[] | null;
      if (vals && vals.length > 0) {
        newEnumFilters[colKey] = vals;
      }
    }
    const enumChanged =
      JSON.stringify(newEnumFilters) !== JSON.stringify(enumFilters);
    if (enumChanged) {
      setEnumFilters(newEnumFilters);
      // useEffect on colFilterKey handles the re-fetch — reset page
      setCurrentPage(1);
      return;
    }

    // ── Sort + pagination ──
    let newSort: { key: string; sort: "asc" | "desc" } | null = null;
    if (sorter.order) {
      const col = COLUMNS.find((c) => c.key === sorter.columnKey);
      const sortKey = col?.originalKey || (sorter.columnKey as string);
      newSort = {
        key: sortKey,
        sort: sorter.order === "ascend" ? "asc" : "desc",
      };
    }
    setCurrentSort(newSort);
    const newPage = pagination.current || 1;
    setCurrentPage(newPage);
    fetchRecords(newPage, newSort);
  };

  // ── Column filter dropdown builder ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getColumnFilterDropdown = (col: BaseColumnDef): Record<string, any> => {
    if (!col.filterKey) return {};
    const fk = col.filterKey;

    // Enum columns → antd native checkbox filters
    if (ENUM_FILTER_OPTIONS[fk]) {
      const selected = enumFilters[col.key] || [];
      return {
        filters: ENUM_FILTER_OPTIONS[fk],
        filteredValue: selected.length > 0 ? selected : null,
        filterIcon: (filtered: boolean): JSX.Element => (
          <FilterOutlined style={{ color: filtered ? darkGreen : undefined }} />
        ),
      };
    }

    // Text columns → custom dropdown (commit on OK / Enter only)
    return {
      filterDropdown: ({ close }: FilterDropdownProps): JSX.Element => {
        const val = draftColumnFilters[fk] || "";
        return (
          <div style={{ padding: 8, width: 220 }}>
            <Input
              placeholder={`Filter ${col.title}`}
              value={val}
              onChange={(e): void =>
                setDraftColumnFilters((prev) => ({
                  ...prev,
                  [fk]: e.target.value,
                }))
              }
              onPressEnter={(): void => {
                const trimmed = val.trim();
                if (trimmed.length > 0) {
                  setColumnFilters((prev) => ({ ...prev, [fk]: trimmed }));
                } else {
                  setColumnFilters((prev) => {
                    const next = { ...prev };
                    delete next[fk];
                    return next;
                  });
                }
                close();
              }}
              style={{
                marginBottom: 8,
                display: "block",
                fontFamily: "Apercu",
              }}
              size="small"
            />
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<SearchOutlined />}
                onClick={(): void => {
                  const trimmed = val.trim();
                  if (trimmed.length > 0) {
                    setColumnFilters((prev) => ({ ...prev, [fk]: trimmed }));
                  } else {
                    setColumnFilters((prev) => {
                      const next = { ...prev };
                      delete next[fk];
                      return next;
                    });
                  }
                  close();
                }}
                style={{ width: 90 }}
              >
                OK
              </Button>
              <Button
                size="small"
                onClick={(): void => {
                  setDraftColumnFilters((prev) => {
                    const next = { ...prev };
                    delete next[fk];
                    return next;
                  });
                  setColumnFilters((prev) => {
                    const next = { ...prev };
                    delete next[fk];
                    return next;
                  });
                  close();
                }}
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean): JSX.Element => (
        <FilterOutlined style={{ color: filtered ? darkGreen : undefined }} />
      ),
      filtered: !!(columnFilters[fk] && columnFilters[fk].trim()),
    };
  };

  const getCellProps = (
    column: BaseColumnDef,
    value: unknown,
  ): CustomTableCellProps => ({
    type: getColumnType(column.key, isLoading).type,
    value: (value as CustomTableCellProps["value"]) || MESSAGES.NOT_AVAILABLE,
  });

  return (
    <Card h="auto" m="0 20px" p="24px" isLoading={false}>
      <RowContainer ai="center" jc="space-between" mb={8}>
        <CompassParagraph bold={true} fontSize={16} color={darkGreen}>
          Linkage Records
        </CompassParagraph>
        <CompassParagraph fontSize={12} color={greyText}>
          {total.toLocaleString()} record{total !== 1 ? "s" : ""}
        </CompassParagraph>
      </RowContainer>
      <CompassParagraph fontSize={12} color={greyText} mb={12}>
        Individual linkages between engagement activities and voting behaviour
      </CompassParagraph>

      <TableContainer p="0">
        <StyledTable
          rowKey="key"
          pagination={{
            pageSize: PAGE_SIZE,
            total,
            current: currentPage,
            showSizeChanger: false,
            showTotal: (t: number, range: [number, number]): string =>
              `Showing ${range[0]}-${range[1]} of ${t} records`,
          }}
          scroll={{ y: "min(52vh, 520px)", x: "max-content" }}
          hasEveryCellBorderBottom={true}
          dataSource={dataSource}
          isLoading={isLoading}
          onChange={(pagination, filters, sorter): void =>
            handleTableChange(
              pagination as { current?: number },
              filters as Record<string, unknown>,
              sorter as SorterResult<AnyObject>,
            )
          }
        >
          {COLUMNS.map((column) => (
            <Table.Column
              key={column.key}
              title={column.title}
              dataIndex={column.dataIndex}
              fixed={column.fixed}
              sorter={column.sorter ? true : false}
              {...getColumnFilterDropdown(column)}
              render={(value: unknown): JSX.Element => {
                if (isLoading) {
                  return <CustomTableCell {...getCellProps(column, value)} />;
                }
                if (column.key === "managers") {
                  const cell = value as DualAvatarCell;
                  return (
                    <FlexibleContainer jc="flex-start" ai="center">
                      <Avatar {...cell.engagementManager} />
                      <span
                        style={{
                          color: "#aaa",
                          fontSize: 13,
                          margin: "0 6px",
                          userSelect: "none",
                        }}
                      >
                        |
                      </span>
                      <Avatar {...cell.votingManager} />
                    </FlexibleContainer>
                  );
                }
                if (AVATAR_TAG_COLUMNS.has(column.key)) {
                  const cell = value as AvatarTagCell;
                  const tagElement = (
                    <FlexibleContainer ml="15px" jc="flex-start" ai="center">
                      {cell.image && (
                        <span style={{ marginRight: 8 }}>
                          <Avatar
                            username={cell.tag.tagKey}
                            image={cell.image}
                            size="default"
                            tooltip={true}
                          />
                        </span>
                      )}
                      <CompassTag {...cell.tag} size="small" />
                    </FlexibleContainer>
                  );
                  if (cell.rationale) {
                    return (
                      <Tooltip title={cell.rationale}>{tagElement}</Tooltip>
                    );
                  }
                  return tagElement;
                }
                return <CustomTableCell {...getCellProps(column, value)} />;
              }}
            />
          ))}
        </StyledTable>
      </TableContainer>
    </Card>
  );
};
